import {
  fetchConferenceAgendaDetail,
  fetchConferenceYearOptions,
  resolveConferenceCoreSectionYears,
} from '../conference/api';
import type {
  ConferenceAgendaDetailResp,
  ConferenceDynamicData,
  ConferenceSite,
} from '../conference/types';

type ResolveConferenceCoreSectionYearsResult = Awaited<
  ReturnType<typeof resolveConferenceCoreSectionYears>
>;

type LoadLatestConferenceAgendaSectionDataOptions = {
  site?: ConferenceSite;
  fetchYearOptions?: typeof fetchConferenceYearOptions;
  fetchAgendaDetail?: typeof fetchConferenceAgendaDetail;
  resolveCoreSectionYears?: typeof resolveConferenceCoreSectionYears;
};

export interface LatestConferenceAgendaSectionData {
  conferenceYear: number;
  data: ConferenceAgendaDetailResp;
}

const EMPTY_CORE_SECTION_DEFAULT_DATA: Pick<
  ConferenceDynamicData,
  'guests' | 'videos'
> = {
  guests: { list: [] },
  videos: { list: [] },
};

export const pickLatestConferenceAgendaSectionData = (
  agendaSection: ResolveConferenceCoreSectionYearsResult['agenda']
): LatestConferenceAgendaSectionData | undefined => {
  const latestYear = agendaSection.years[0];
  if (!latestYear) {
    return undefined;
  }

  const latestData = agendaSection.dataByYear.get(latestYear);
  if (!latestData) {
    return undefined;
  }

  return {
    conferenceYear: latestYear,
    data: latestData,
  };
};

export const loadLatestConferenceAgendaSectionData = async ({
  site = 'zh',
  fetchYearOptions = fetchConferenceYearOptions,
  fetchAgendaDetail = fetchConferenceAgendaDetail,
  resolveCoreSectionYears = resolveConferenceCoreSectionYears,
}: LoadLatestConferenceAgendaSectionDataOptions = {}) => {
  const yearData = await fetchYearOptions(site);
  const defaultYear =
    yearData.DefaultYear || yearData.Years?.[0] || new Date().getFullYear();
  // 会聊页新增的大会议程必须与官网大会页保持同一套“默认年份 + 有数据年份过滤”逻辑。
  const defaultAgendaData = await fetchAgendaDetail({
    conferenceYear: defaultYear,
    site,
  });
  const coreSectionYears = await resolveCoreSectionYears({
    guestYears: [],
    agendaYears: yearData.AgendaYears || [],
    videoYears: [],
    defaultYear,
    defaultData: {
      ...EMPTY_CORE_SECTION_DEFAULT_DATA,
      agenda: defaultAgendaData,
    },
    site,
  });

  return pickLatestConferenceAgendaSectionData(coreSectionYears.agenda);
};
